package com.sddamico.components.motion

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.Element
import org.w3c.dom.HTMLElement

external class ResizeObserver(callback: (entries: Array<dynamic>, observer: ResizeObserver) -> Unit) {
    fun observe(target: Element)
    fun disconnect()
}

/**
 * Animates its own height to follow its content, so content that grows or shrinks (a loaded list replacing a
 * placeholder, a form step swapping in) slides open instead of jumping.
 */
@Composable
fun AnimatedHeight(modifier: Modifier = Modifier, content: @Composable () -> Unit) {
    HeightContainer(open = true, modifier = modifier, contentOpacity = null, content = content)
}

/**
 * Expands and collapses [content]. The content stays mounted until the collapse finishes and fades while it moves.
 */
@Composable
fun Collapsible(expanded: Boolean, modifier: Modifier = Modifier, content: @Composable () -> Unit) {
    val presence = rememberPresence(expanded, exitMillis = Durations.MOVE)
    if (!presence.isMounted) return
    HeightContainer(
        open = presence.isShown,
        modifier = modifier,
        contentOpacity = if (presence.isShown) 1.0 else 0.0,
        content = content,
    )
}

@Composable
private fun HeightContainer(
    open: Boolean,
    modifier: Modifier,
    contentOpacity: Double?,
    content: @Composable () -> Unit,
) {
    val reducedMotion = rememberReducedMotion()
    var measuredHeight by remember { mutableStateOf<Double?>(null) }

    val height = when {
        !open -> "0px"
        measuredHeight == null -> "auto"
        else -> "${measuredHeight}px"
    }

    Div(
        modifier
            .fillMaxWidth()
            .styleModifier {
                property("height", height)
                property("overflow", "clip")
                if (!reducedMotion) property("transition", "height ${Durations.MOVE}ms ${Ease.Out.css}")
            }
            .toAttrs()
    ) {
        Div(
            Modifier
                .fillMaxWidth()
                .styleModifier {
                    // A collapsed child's margins would otherwise leak out of the 0px container.
                    property("display", "flow-root")
                    if (contentOpacity != null) {
                        property("opacity", contentOpacity.toString())
                        property(
                            "transition",
                            if (contentOpacity > 0) "opacity 200ms ${Ease.Out.css} 60ms" else "opacity 120ms ${Ease.Out.css}"
                        )
                    }
                }
                .toAttrs {
                    ref { element ->
                        val observer = ResizeObserver { _, _ -> measuredHeight = (element as HTMLElement).offsetHeight.toDouble() }
                        observer.observe(element)
                        onDispose { observer.disconnect() }
                    }
                }
        ) {
            content()
        }
    }
}
